import { useCallback, useEffect, useState } from "react";
import {
  approveServiceRequest,
  declineServiceRequest,
  listServiceOfferings,
  listServiceRequests,
  listServiceWorkQueues,
  sendBackServiceRequest,
} from "@/lib/service/client";
import {
  draftFromServiceRequest,
  type ServiceRequestOperationalDraft,
} from "@/lib/service/requestApprovalClient";
import type { ServiceOffering, ServiceRequest, ServiceWorkQueue } from "@/lib/service/types";

export function useServiceRequestsPanel(navTick: number) {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [queues, setQueues] = useState<ServiceWorkQueue[]>([]);
  const [offerings, setOfferings] = useState<ServiceOffering[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [operationalDrafts, setOperationalDrafts] = useState<
    Record<string, ServiceRequestOperationalDraft>
  >({});

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [reqRes, queueRes, offeringRes] = await Promise.all([
      listServiceRequests({ status: "pending" }),
      listServiceWorkQueues({ active_only: true }),
      listServiceOfferings({ active_only: true }),
    ]);
    const needsInfoRes = await listServiceRequests({ status: "needs_info" });
    setListBusy(false);
    if (!reqRes.ok) {
      setLocalError(reqRes.error);
      setRequests([]);
      return;
    }
    const merged = [...reqRes.data.requests];
    if (needsInfoRes.ok) {
      for (const row of needsInfoRes.data.requests) {
        if (!merged.some((r) => r.id === row.id)) merged.push(row);
      }
    }
    if (!queueRes.ok) {
      setLocalError(queueRes.error);
      setQueues([]);
      setOfferings(offeringRes.ok ? offeringRes.data.offerings : []);
      setRequests(merged);
      setOperationalDrafts(Object.fromEntries(merged.map((row) => [row.id, draftFromServiceRequest(row)])));
      return;
    }
    setRequests(merged);
    setQueues(queueRes.data.queues);
    setOfferings(offeringRes.ok ? offeringRes.data.offerings : []);
    setOperationalDrafts(Object.fromEntries(merged.map((row) => [row.id, draftFromServiceRequest(row)])));
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const setDraft = useCallback(
    (requestId: string, patch: Partial<ServiceRequestOperationalDraft>) => {
      setOperationalDrafts((prev) => {
        const req = requests.find((r) => r.id === requestId);
        const base = prev[requestId] ?? (req ? draftFromServiceRequest(req) : {
          description: "",
          location_label: "",
          location_address: "",
          queue_id: null,
        });
        return { ...prev, [requestId]: { ...base, ...patch } };
      });
    },
    [requests]
  );

  const runAction = useCallback(
    async (requestId: string, action: "approve" | "decline" | "needs_info") => {
      setActionBusyId(requestId);
      setLocalError(null);
      const notes = reviewNotes[requestId]?.trim() ?? "";
      const draft = operationalDrafts[requestId] ?? draftFromServiceRequest(
        requests.find((r) => r.id === requestId) ?? ({ id: requestId } as ServiceRequest)
      );
      const payload = {
        request_id: requestId,
        review_notes: notes,
        description: draft.description,
        location_label: draft.location_label,
        location_address: draft.location_address,
        queue_id: draft.queue_id,
      };
      const res =
        action === "approve"
          ? await approveServiceRequest(payload)
          : action === "decline"
            ? await declineServiceRequest(payload)
            : await sendBackServiceRequest({ ...payload, review_notes: notes });
      setActionBusyId(null);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload, reviewNotes, operationalDrafts, requests]
  );

  return {
    requests,
    queues,
    offerings,
    listBusy,
    actionBusyId,
    localError,
    reviewNotes,
    setReviewNotes,
    operationalDrafts,
    setDraft,
    runAction,
    reload,
  };
}
